/* Page-specific tabs and the legacy accordion scroll option. Shared slider and
   exclusive FAQ controllers remain in kw-native-pages.js, unmodified. */
(() => {
  'use strict';
  if (window.__kwPageLinkedMediaV1) return;
  window.__kwPageLinkedMediaV1 = { installed: true };
  const roots = new WeakMap();
  const selections = new Map();
  const selector = '[data-kw-linked-media], [data-kw-editorial]';
  const matchingRoots = scope => [
    ...(scope instanceof Element && scope.matches(selector) ? [scope] : []),
    ...scope.querySelectorAll(selector)
  ];
  const install = root => {
    if (roots.has(root)) return;
    const abort = new AbortController();
    const on = (node, type, callback) => node.addEventListener(type, callback, { signal: abort.signal });
    const cleanup = () => {
      abort.abort();
      root.removeAttribute('data-tabs-enhanced');
      root.querySelectorAll('[data-kw-media-panel]').forEach(panel => {
        panel.hidden = false;
        panel.removeAttribute('role');
        panel.removeAttribute('aria-labelledby');
      });
      const nav = root.querySelector('[data-kw-media-nav]');
      if (nav) nav.hidden = true;
      roots.delete(root);
    };
    roots.set(root, cleanup);
    try {
      if (root.hasAttribute('data-kw-editorial')) {
        let requested = null;
        on(root, 'click', event => {
          const summary = event.target.closest('summary');
          requested = summary?.parentElement instanceof HTMLDetailsElement ? summary.parentElement : null;
        });
        root.querySelectorAll('details').forEach(details => on(details, 'toggle', () => {
          if (!details.open || details !== requested) return;
          requested = null;
          if (root.querySelector('[data-kw-scroll-to-top]')?.dataset.kwScrollToTop !== 'true') return;
          if (window.Shopify?.designMode === true) return;
          const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          details.scrollIntoView({ block: 'start', behavior: reduced ? 'instant' : 'smooth' });
        }));
        return;
      }
      if (root.dataset.kwMediaMode !== 'tabs') return;
      const nav = root.querySelector('[data-kw-media-nav]');
      const list = root.querySelector('[data-kw-media-tablist]');
      const tabs = Array.from(root.querySelectorAll('[data-kw-media-tab]'));
      const panels = Array.from(root.querySelectorAll('[data-kw-media-panel]'));
      if (!nav || !list || !tabs.length || tabs.length !== panels.length) return;
      const unique = new Set(panels.map(panel => panel.id));
      if (unique.size !== panels.length || panels.some(panel => !panel.id)) return;
      let selected = Math.max(0, tabs.findIndex(tab => tab.dataset.kwMediaCategory === selections.get(root.id)));
      const previous = nav.querySelector('[data-kw-media-previous]');
      const next = nav.querySelector('[data-kw-media-next]');
      const select = (index, focus = false) => {
        selected = Math.max(0, Math.min(tabs.length - 1, index));
        tabs.forEach((tab, at) => {
          const active = at === selected;
          tab.setAttribute('aria-selected', String(active));
          tab.tabIndex = active ? 0 : -1;
          panels[at].hidden = !active;
        });
        selections.set(root.id, tabs[selected].dataset.kwMediaCategory);
        if (previous) previous.disabled = selected === 0;
        if (next) next.disabled = selected === tabs.length - 1;
        // Move only the tab strip, never the document, when navigating a category.
        const tab = tabs[selected];
        const before = tab.offsetLeft - list.offsetLeft;
        if (before < list.scrollLeft) list.scrollLeft = before;
        else if (before + tab.offsetWidth > list.scrollLeft + list.clientWidth) list.scrollLeft = before + tab.offsetWidth - list.clientWidth;
        if (focus) tab.focus({ preventScroll: true });
      };
      list.setAttribute('role', 'tablist');
      tabs.forEach((tab, index) => {
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', panels[index].id);
        panels[index].setAttribute('role', 'tabpanel');
        panels[index].setAttribute('aria-labelledby', tab.id);
        on(tab, 'click', () => select(index));
        on(tab, 'keydown', event => {
          let at = index;
          if (event.key === 'ArrowLeft') at = Math.max(0, index - 1);
          else if (event.key === 'ArrowRight') at = Math.min(tabs.length - 1, index + 1);
          else if (event.key === 'Home') at = 0;
          else if (event.key === 'End') at = tabs.length - 1;
          else return;
          event.preventDefault();
          select(at, true);
        });
      });
      if (previous) on(previous, 'click', () => select(selected - 1, true));
      if (next) on(next, 'click', () => select(selected + 1, true));
      on(root, 'shopify:block:select', event => {
        const card = event.target.closest('[data-kw-media-category]');
        if (!card) return;
        const index = tabs.findIndex(tab => tab.dataset.kwMediaCategory === card.dataset.kwMediaCategory);
        if (index >= 0) select(index);
      });
      root.setAttribute('data-tabs-enhanced', '');
      nav.hidden = false;
      select(selected);
    } catch (_error) {
      // Keep every panel and native link reachable if enhancement cannot start.
      cleanup();
    }
  };
  const init = scope => matchingRoots(scope).forEach(install);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => matchingRoots(event.target).forEach(root => roots.get(root)?.()));
})();
